import React, { useState, useEffect, useRef, useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Grid,
    InputAdornment,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableRow,
    TableCell
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, Close as CloseIcon } from '@mui/icons-material';
import toastr from 'toastr';
import * as LegacyViewerService from '../../../services/clinicPortalServices/legacyViewerService';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import DetailCellRenderer from './detailCellRenderer';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

const GlobalSearch = () => {
    const gridRef = useRef();
    const { themeName } = useContext(ThemeContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [entities, setEntities] = useState([]);
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [detailDialog, setDetailDialog] = useState({ open: false, data: null });

    // AG-Grid column definitions
    const [columnDefs] = useState([
        {
            headerName: 'Entity Type',
            field: 'entityType',
            width: 150,
            valueFormatter: (params) => (params.value ? params.value.replace(/_/g, ' ') : '')
        },
        {
            headerName: 'File Name',
            field: 'fileName',
            width: 200
        },
        {
            headerName: 'Row',
            field: 'rowIndex',
            width: 100
        },
        {
            headerName: 'Matched Fields',
            field: 'matchedFields',
            width: 200,
            valueFormatter: (params) => (params.value ? params.value.join(', ') : '')
        },
        {
            headerName: 'Data Preview',
            field: 'dataPreview',
            flex: 1,
            autoHeight: true,
            wrapText: true,
            cellRenderer: (params) => {
                if (!params.data.data) return '';
                const entries = Object.entries(params.data.data).slice(0, 3);
                return entries.map(([key, value]) => `${key}: ${value || '-'}`).join(' | ');
            }
        },
        {
            headerName: 'Actions',
            width: 100,
            cellRenderer: DetailCellRenderer,
            cellRendererParams: {
                onViewDetails: (data) => setDetailDialog({ open: true, data })
            },
            filter: false,
            sortable: false
        }
    ]);

    const defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true
    };

    // Components for AG-Grid
    const components = {
        detailCellRenderer: DetailCellRenderer
    };

    useEffect(() => {
        loadEntities();
    }, []);

    const loadEntities = async () => {
        try {
            const response = await LegacyViewerService.getEntityList();
            if (response.success) {
                setEntities(response.data.map((e) => e.entityType));
            }
        } catch (error) {
            console.error('Failed to load entities');
        }
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            toastr.warning('Please enter a search term');
            return;
        }

        try {
            setSearching(true);
            setHasSearched(true);

            const response = await LegacyViewerService.searchAcrossFiles(searchTerm, {
                entityTypes: selectedEntities,
                limit: 500,
                useDatabase: true
            });

            if (response.success) {
                console.log('Search response:', response);
                console.log('Search results data:', response.data);

                setResults(response.data);
                setRowData(response.data);

                if (response.data.length === 0) {
                    toastr.info('No results found');
                } else {
                    toastr.success(`Found ${response.data.length} results`);

                    // Log first result for debugging
                    if (response.data.length > 0) {
                        console.log('First result:', response.data[0]);
                    }
                }
            }
        } catch (error) {
            toastr.error('Search failed');
            console.error(error);
        } finally {
            setSearching(false);
        }
    };

    const handleEntityToggle = (entity) => {
        setSelectedEntities((prev) => {
            if (prev.includes(entity)) {
                return prev.filter((e) => e !== entity);
            } else {
                return [...prev, entity];
            }
        });
    };

    const clearSearch = () => {
        setSearchTerm('');
        setResults([]);
        setRowData([]);
        setHasSearched(false);
    };

    const onGridReady = (params) => {
        gridRef.current = params.api;
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Global Search
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Search across all legacy data files
            </Typography>

            {/* Search Controls */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Search Term"
                            placeholder="Enter patient name, ID, test result, etc..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={clearSearch} size="small">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                            Filter by Entity Types (leave empty to search all)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {entities.map((entity) => (
                                <Chip
                                    key={entity}
                                    label={entity.replace(/_/g, ' ')}
                                    onClick={() => handleEntityToggle(entity)}
                                    color={selectedEntities.includes(entity) ? 'primary' : 'default'}
                                    variant={selectedEntities.includes(entity) ? 'filled' : 'outlined'}
                                />
                            ))}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" gap={2}>
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                disabled={searching || !searchTerm.trim()}
                                startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                            >
                                {searching ? 'Searching...' : 'Search'}
                            </Button>
                            {selectedEntities.length > 0 && (
                                <Button variant="outlined" onClick={() => setSelectedEntities([])}>
                                    Clear Filters
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Search Results */}
            {hasSearched && (
                <Paper sx={{ height: 'calc(100vh - 450px)', minHeight: '400px' }}>
                    {results.length === 0 ? (
                        <Alert severity="info" sx={{ m: 2 }}>
                            No results found for "{searchTerm}"
                            {selectedEntities.length > 0 && ` in ${selectedEntities.join(', ')}`}
                        </Alert>
                    ) : (
                        <Box sx={{ height: '100%' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                                <Typography variant="h6">Search Results ({results.length})</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Click on any row to view full details
                                </Typography>
                            </Box>

                            <div id="searchGrid" style={{ height: 'calc(100% - 64px)', width: '100%' }}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    components={components}
                                    pagination={true}
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[50, 100, 250, 500]}
                                    onGridReady={onGridReady}
                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                    enableCellTextSelection={true}
                                    ensureDomOrder={true}
                                    suppressMenuHide={true}
                                    rowSelection="single"
                                    animateRows={true}
                                    onRowDoubleClicked={(params) => {
                                        setDetailDialog({ open: true, data: params.data });
                                    }}
                                />
                            </div>
                        </Box>
                    )}
                </Paper>
            )}

            {/* Detail Dialog */}
            <Dialog
                open={detailDialog.open}
                onClose={() => setDetailDialog({ open: false, data: null })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Search Result Details</Typography>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => setDetailDialog({ open: false, data: null })}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {detailDialog.data && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Entity: {detailDialog.data.entityType?.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="subtitle2" gutterBottom>
                                File: {detailDialog.data.fileName}
                            </Typography>
                            <Typography variant="subtitle2" gutterBottom>
                                Row: {detailDialog.data.rowIndex}
                            </Typography>
                            <Typography variant="subtitle2" gutterBottom>
                                Matched Fields: {detailDialog.data.matchedFields?.join(', ')}
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                Data Fields
                            </Typography>
                            <Table size="small">
                                <TableBody>
                                    {Object.entries(detailDialog.data.data || {}).map(([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                sx={{ fontWeight: 'bold', width: '40%' }}
                                            >
                                                {key}
                                            </TableCell>
                                            <TableCell>{value || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog({ open: false, data: null })}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GlobalSearch;
